  import { apiClient } from "@/shared/services/api";
  import type { AuthUser } from "../stores/authStore";

  export interface LoginRequest {
    usernameOrEmail: string;
    password: string;
  }

  export interface ResultWarning {
    code: string;
    message: string;
    data?: Record<string, unknown> | null;
  }

  export interface ApiResponse<T> {
    success: boolean;
    message: string;
    errorCode: null | string;
    errors: null | Record<string, string[]>;
    data: T;
    warnings?: ResultWarning[] | null;
  }

  export interface LoginData {
    user: AuthUser;
    token: string;
    expiresAt: string;
    refreshToken: string;
    refreshTokenExpiresAt: string;
    // The backend now ships the user's permission list alongside the token so the
    // JWT can stay small. Older tokens may not include this — frontend falls back
    // to decoding the JWT in that case.
    permissions?: string[];
  }

  export interface OtpRequiredData {
    otpToken: string;
    maskedPhone: string;
    expiresAt: string;
    codeLength: number;
  }

  export interface LoginStepData {
    requiresOtp: boolean;
    otpChallenge: OtpRequiredData | null;
    loginData: LoginData | null;
  }

  export interface VerifyOtpRequest {
    otpToken: string;
    code: string;
  }

  export interface ResendOtpRequest {
    otpToken: string;
  }

  export interface OtpResendData {
    expiresAt: string;
    cooldownSeconds: number;
  }

  export interface RefreshTokenRequest {
    refreshToken: string;
  }

  export type LoginResponse = ApiResponse<LoginStepData>;

  export interface UserSearchResult {
    id: string;
    username: string;
    firstName: string;
    otherNames: string;
    email: string;
    phoneNumber: string;
    isActive: boolean;
    emailVerified: boolean;
    phoneVerified: boolean;
    lastLoginAt: string | null;
    roles: string[];
    createdAt: string;
  }

  export interface UserSearchResponse {
    users: UserSearchResult[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
  }

  export const authApi = {
    login: async (credentials: LoginRequest): Promise<LoginStepData> => {
      const { data } = await apiClient.post<LoginResponse>(
        '/Users/login',
        credentials
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Login failed');
      }

      return data.data;
    },

    verifyOtp: async (request: VerifyOtpRequest): Promise<LoginData> => {
      const { data } = await apiClient.post<ApiResponse<LoginData>>(
        '/Users/verify-otp',
        request
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Verification failed');
      }
      return data.data;
    },

    resendOtp: async (request: ResendOtpRequest): Promise<OtpResendData> => {
      const { data } = await apiClient.post<ApiResponse<OtpResendData>>(
        '/Users/resend-otp',
        request
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Failed to resend code');
      }
      return data.data;
    },

    logout: async (refreshToken: string): Promise<void> => {
      await apiClient.post('/Users/logout', { refreshToken } satisfies RefreshTokenRequest);
    },

    getCurrentUser: async () => {
      const { data } = await apiClient.get<AuthUser>('/auth/me');
      return data;
    },

    searchUsers: async (
      name: string,
      pageNumber: number = 1,
      pageSize: number = 10,
      signal?: AbortSignal
    ): Promise<UserSearchResponse> => {
      const { data } = await apiClient.get<ApiResponse<UserSearchResponse>>(
        '/Users/search',
        {
          params: { name, pageNumber, pageSize },
          signal
        }
      );
      return data.data;
    }

  };
